/**
 * SubAttendanceCard — sub check-in/out history for one deal (Deal Detail).
 * Renders nothing when the job has no sub punches yet.
 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { HardHat } from "lucide-react";

const mtTime = (ts) => ts ? new Date(ts).toLocaleTimeString("en-US", { timeZone: "America/Denver", hour: "numeric", minute: "2-digit" }) : "—";
const mtDate = (ts) => new Date(ts).toLocaleDateString("en-US", { timeZone: "America/Denver", month: "short", day: "numeric" });

const Flag = ({ onsite, dist }) => {
  if (onsite === true) return <span className="text-emerald-700 font-bold">✓</span>;
  if (onsite === false) return <span className="text-amber-600 font-bold" title={`${dist} mi from site`}>⚠ {dist}mi</span>;
  return null;
};

export const SubAttendanceCard = ({ dealId }) => {
  const [rows, setRows] = useState([]);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    api.get(`/subclock/log?deal_id=${dealId}`).then((r) => setRows(r.data || [])).catch(() => {});
  }, [dealId]);

  if (rows.length === 0) return null;
  const shown = expanded ? rows : rows.slice(0, 5);

  return (
    <div className="border border-zinc-200 bg-white rounded-sm px-3 py-2 mb-6" data-testid="deal-sub-attendance-card">
      <div className="flex items-center gap-2 mb-1.5">
        <HardHat className="w-3.5 h-3.5 text-blue-700" />
        <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Sub Attendance</span>
        <span className="text-[10px] text-zinc-400">{rows.length} check-in{rows.length === 1 ? "" : "s"}</span>
      </div>
      <div className="space-y-0.5">
        {shown.map((r) => (
          <div key={r.id} className="text-xs text-zinc-700 flex flex-wrap items-center gap-x-2" data-testid={`sub-att-row-${r.id}`}>
            <span className="font-mono text-[11px] text-zinc-500 w-14">{mtDate(r.check_in)}</span>
            <span className="font-medium">{r.vendor_name}</span>
            {r.foreman_name && <span className="text-zinc-500">({r.foreman_name})</span>}
            <span className="font-mono">{mtTime(r.check_in)}</span>
            <Flag onsite={r.on_site_in} dist={r.distance_in_mi} />
            <span className="text-zinc-400">→</span>
            {r.check_out ? (
              <>
                <span className="font-mono">{mtTime(r.check_out)}</span>
                <Flag onsite={r.on_site_out} dist={r.distance_out_mi} />
                <span className="text-zinc-500">· {r.hours}h</span>
              </>
            ) : (
              <span className="text-emerald-700 font-bold">on site</span>
            )}
          </div>
        ))}
      </div>
      {rows.length > 5 && (
        <button onClick={() => setExpanded(!expanded)} data-testid="sub-att-toggle"
          className="mt-1.5 text-[10px] font-bold uppercase tracking-wider text-blue-700 hover:underline">
          {expanded ? "Show less" : `Show all ${rows.length}`}
        </button>
      )}
    </div>
  );
};

export default SubAttendanceCard;
